import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    FormControlLabel,
    IconButton,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import {
    type OfficeHoursDateOverride,
    type OfficeHoursDayAvailability,
    type SandboxOfficeHoursConfig,
} from '../../utils/sandboxOfficeHoursApi';
import {
    fetchMyOfficeHours,
    fetchOfficeHoursGoogleAuthorizeUrl,
    upsertMyOfficeHours,
} from '../../redux/sandbox/actions';
import { SandboxOfficeHoursWeeklyEditor, officeHoursFieldSx } from './SandboxOfficeHoursWeeklyEditor';
import { SandboxOfficeHoursOverridesEditor } from './SandboxOfficeHoursOverridesEditor';
import { SandboxOfficeHoursHostBookingsPanel } from './SandboxOfficeHoursHostBookingsPanel';
import { getBrowserTimezone, listTimezoneOptions, formatTimezoneOptionLabel } from '../../utils/timezoneOptions';

const DURATIONS = [15, 30, 45, 60, 90, 120];

function defaultAvailability(): OfficeHoursDayAvailability[] {
    return [0, 1, 2, 3, 4, 5, 6].map(day => ({
        day,
        enabled: day >= 1 && day <= 5,
        slots: day >= 1 && day <= 5 ? [{ start: '09:00', end: '17:00', price_amount: null }] : [],
    }));
}

function stepKicker(n: string, label: string) {
    return (
        <Box sx={{ mb: 1.25 }}>
            <Typography
                sx={{
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: OBSIDIAN.primary,
                    fontFamily: OBSIDIAN_BODY_FONT,
                }}>
                Step {n}
            </Typography>
            <Typography
                sx={{
                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                    fontWeight: 700,
                    fontSize: 16,
                    color: OBSIDIAN.onSurface,
                }}>
                {label}
            </Typography>
        </Box>
    );
}

const panelSx = {
    p: 2,
    borderRadius: 2.5,
    border: `1px solid ${OBSIDIAN.outline}44`,
    bgcolor: OBSIDIAN.surfaceContainerHigh,
};

type Props = {
    open: boolean;
    onClose: () => void;
};

/**
 * Teacher office hours — Meeteor-style setup + overrides, Obsidian look.
 */
export function SandboxOfficeHoursModal({ open, onClose }: Props) {
    const dispatchPromise = useDispatchPromise();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [googleConnected, setGoogleConnected] = useState(false);
    const [hostEmail, setHostEmail] = useState('');
    const [title, setTitle] = useState('Office Hours');
    const [description, setDescription] = useState('');
    const [duration, setDuration] = useState(30);
    const [bufferBefore, setBufferBefore] = useState(0);
    const [bufferAfter, setBufferAfter] = useState(0);
    const [timezone, setTimezone] = useState(getBrowserTimezone);
    const [availability, setAvailability] = useState<OfficeHoursDayAvailability[]>(defaultAvailability);
    const [dateOverrides, setDateOverrides] = useState<OfficeHoursDateOverride[]>([]);
    const [pricingEnabled, setPricingEnabled] = useState(false);
    const [defaultPrice, setDefaultPrice] = useState('0');
    const [billing, setBilling] = useState<'per_slot' | 'per_hour'>('per_slot');
    const [active, setActive] = useState(true);
    const [viewTab, setViewTab] = useState<'setup' | 'bookings'>('setup');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res: any = await dispatchPromise(fetchMyOfficeHours());
            const cfg = (res?.config || null) as SandboxOfficeHoursConfig | null;
            const browserTz = getBrowserTimezone();
            setGoogleConnected(Boolean(res?.google_connected));
            if (cfg) {
                setTitle(cfg.title || 'Office Hours');
                setDescription(cfg.description || '');
                setDuration(cfg.duration || 30);
                setBufferBefore(Number(cfg.buffer_before) || 0);
                setBufferAfter(Number(cfg.buffer_after) || 0);
                // Prefer saved TZ once cadence is set. Unset / legacy UTC / never configured → local.
                const stored = (cfg.timezone || '').trim();
                const neverConfigured = !(cfg.availability || []).some(
                    d => d.enabled && Array.isArray(d.slots) && d.slots.length > 0,
                );
                setTimezone(!stored || stored === 'UTC' || neverConfigured ? browserTz : stored);
                setHostEmail(cfg.host_email || '');
                setAvailability(cfg.availability?.length ? cfg.availability : defaultAvailability());
                setDateOverrides(cfg.date_overrides || []);
                setPricingEnabled(Boolean(cfg.pricing?.enabled));
                setDefaultPrice(String(cfg.pricing?.amount ?? 0));
                setBilling(cfg.pricing?.billing === 'per_hour' ? 'per_hour' : 'per_slot');
                setActive(cfg.active !== false);
            } else {
                setTimezone(browserTz);
            }
        } catch (e: any) {
            showToastMessage(e?.message || 'Could not load office hours', 'error');
        } finally {
            setLoading(false);
        }
    }, [dispatchPromise]);

    useEffect(() => {
        if (open) {
            setViewTab('setup');
            void load();
        }
    }, [open, load]);

    const activeDays = useMemo(() => availability.filter(d => d.enabled).length, [availability]);

    const timezoneOptions = useMemo(() => listTimezoneOptions(timezone), [timezone]);

    const connectGoogle = async () => {
        setConnecting(true);
        try {
            const res: any = await dispatchPromise(fetchOfficeHoursGoogleAuthorizeUrl());
            const url = res?.url || res?.data?.url;
            if (!url) throw new Error('Could not start Google connect');
            window.location.href = url;
        } catch (e: any) {
            showToastMessage(e?.message || 'Google connect failed', 'error');
            setConnecting(false);
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            const amount = Number(defaultPrice) || 0;
            await dispatchPromise(
                upsertMyOfficeHours({
                    title: title.trim() || 'Office Hours',
                    description: description.trim() || undefined,
                    duration,
                    buffer_before: Math.max(0, Number(bufferBefore) || 0),
                    buffer_after: Math.max(0, Number(bufferAfter) || 0),
                    timezone,
                    availability,
                    date_overrides: dateOverrides,
                    create_meet_link: true,
                    active,
                    pricing: {
                        enabled: pricingEnabled && amount > 0,
                        amount: pricingEnabled ? amount : 0,
                        currency: 'usd',
                        billing,
                    },
                }),
            );
            showToastMessage('Office hours saved', 'success');
            await load();
            onClose();
        } catch (e: any) {
            showToastMessage(e?.message || 'Save failed', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            fullScreen
            sx={{
                '& .MuiDialog-container': {
                    alignItems: 'stretch',
                    justifyContent: 'stretch',
                },
                '& .MuiPaper-root': {
                    m: '0 !important',
                    width: '100vw !important',
                    maxWidth: '100vw !important',
                    height: '100dvh !important',
                    maxHeight: '100dvh !important',
                    borderRadius: '0 !important',
                },
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        bgcolor: 'rgba(0,0,0,0.35)',
                        backdropFilter: 'blur(14px)',
                        WebkitBackdropFilter: 'blur(14px)',
                    },
                },
            }}
            PaperProps={{
                sx: {
                    bgcolor: 'rgba(14,14,14,0.88)',
                    backgroundImage: 'none',
                    borderRadius: 0,
                    border: 'none',
                    boxShadow: 'none',
                    m: 0,
                    width: '100%',
                    maxWidth: '100%',
                    height: '100%',
                    maxHeight: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                },
            }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: { xs: 2, md: 3, lg: 4 },
                    py: 2,
                    borderBottom: `1px solid ${OBSIDIAN.outline}33`,
                    flexShrink: 0,
                    width: '100%',
                }}>
                <Box>
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                            fontWeight: 800,
                            fontSize: 20,
                            color: OBSIDIAN.onSurface,
                        }}>
                        Office hours
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 12,
                            color: OBSIDIAN.onSurfaceVariant,
                            fontFamily: OBSIDIAN_BODY_FONT,
                            mt: 0.25,
                        }}>
                        {viewTab === 'bookings'
                            ? 'Student sessions with notes and linked projects.'
                            : 'Set availability, pricing, and Google Calendar.'}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} disabled={saving} size="small" sx={{ color: OBSIDIAN.onSurfaceVariant }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8, flex: 1 }}>
                    <CircularProgress sx={{ color: OBSIDIAN.primary }} />
                </Box>
            ) : (
                <>
                    <Box sx={{ px: { xs: 2, md: 3, lg: 4 }, pt: 2, flexShrink: 0 }}>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                p: 0.5,
                                borderRadius: 999,
                                bgcolor: OBSIDIAN.surfaceContainer,
                                gap: 0.5,
                            }}>
                            {(
                                [
                                    { id: 'setup' as const, label: 'Setup' },
                                    { id: 'bookings' as const, label: 'Bookings' },
                                ] as const
                            ).map(t => {
                                const on = viewTab === t.id;
                                return (
                                    <Button
                                        key={t.id}
                                        onClick={() => setViewTab(t.id)}
                                        sx={{
                                            minWidth: 0,
                                            px: 2,
                                            py: 0.75,
                                            borderRadius: 999,
                                            textTransform: 'none',
                                            fontFamily: OBSIDIAN_BODY_FONT,
                                            fontWeight: 700,
                                            fontSize: 13,
                                            color: on ? OBSIDIAN.onPrimaryFixed : OBSIDIAN.onSurfaceVariant,
                                            bgcolor: on ? OBSIDIAN.secondary : 'transparent',
                                            boxShadow: on ? '0 2px 10px rgba(0,253,193,0.25)' : 'none',
                                        }}>
                                        {t.label}
                                    </Button>
                                );
                            })}
                        </Box>
                    </Box>

                    {viewTab === 'bookings' ? (
                        <Box
                            sx={{
                                px: { xs: 2, md: 3, lg: 4 },
                                py: 2,
                                overflowY: 'auto',
                                flex: 1,
                                minHeight: 0,
                            }}>
                            <SandboxOfficeHoursHostBookingsPanel active={open && viewTab === 'bookings'} />
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                px: { xs: 2, md: 3, lg: 4 },
                                py: 2,
                                overflowY: 'auto',
                                flex: 1,
                                minHeight: 0,
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '1.15fr 0.85fr' },
                                gap: 2,
                                alignItems: 'stretch',
                                width: '100%',
                                boxSizing: 'border-box',
                            }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                                <Box sx={panelSx}>
                                    {stepKicker('01', 'Foundational intel')}
                                    <Box
                                        sx={{
                                            p: 1.5,
                                            mb: 1.5,
                                            borderRadius: 2,
                                            bgcolor: OBSIDIAN.surfaceContainer,
                                            border: `1px solid ${OBSIDIAN.outline}44`,
                                        }}>
                                        <Typography
                                            sx={{ fontSize: 13, fontWeight: 600, color: OBSIDIAN.onSurface, mb: 0.5 }}>
                                            Google Calendar & Meet
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: OBSIDIAN.onSurfaceVariant, mb: 1.25 }}>
                                            {googleConnected
                                                ? `Connected as ${hostEmail || 'your Google account'}`
                                                : 'Connect any Google account so bookings create a Meet link on your calendar.'}
                                        </Typography>
                                        <Button
                                            disabled={connecting}
                                            onClick={() => void connectGoogle()}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                borderRadius: 999,
                                                bgcolor: googleConnected ? 'transparent' : OBSIDIAN.primary,
                                                color: googleConnected ? OBSIDIAN.primary : OBSIDIAN.onPrimaryFixed,
                                                border: googleConnected ? `1px solid ${OBSIDIAN.primary}66` : 'none',
                                                px: 2,
                                            }}>
                                            {connecting
                                                ? 'Opening…'
                                                : googleConnected
                                                ? 'Reconnect Google'
                                                : 'Connect Google'}
                                        </Button>
                                    </Box>

                                    <TextField
                                        label="Title"
                                        value={title}
                                        onChange={e => setTitle(e.target.value)}
                                        fullWidth
                                        sx={{ ...officeHoursFieldSx, mb: 1.5 }}
                                    />
                                    <TextField
                                        label="Description (optional)"
                                        value={description}
                                        onChange={e => setDescription(e.target.value)}
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        sx={{ ...officeHoursFieldSx, mb: 1.5 }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                                        <TextField
                                            select
                                            label="Slot length"
                                            value={duration}
                                            onChange={e => setDuration(Number(e.target.value))}
                                            fullWidth
                                            sx={officeHoursFieldSx}>
                                            {DURATIONS.map(d => (
                                                <MenuItem key={d} value={d}>
                                                    {d} min
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <TextField
                                            select
                                            label="Timezone"
                                            value={timezone}
                                            onChange={e => setTimezone(e.target.value)}
                                            fullWidth
                                            helperText="Defaults to your current timezone"
                                            FormHelperTextProps={{ sx: { color: OBSIDIAN.onSurfaceVariant } }}
                                            sx={{
                                                ...officeHoursFieldSx,
                                                '& .MuiOutlinedInput-root': {
                                                    ...officeHoursFieldSx['& .MuiOutlinedInput-root'],
                                                    fontWeight: 700,
                                                    color: OBSIDIAN.onSurface,
                                                },
                                                '& .MuiSelect-select': {
                                                    fontWeight: 700,
                                                    color: OBSIDIAN.onSurface,
                                                },
                                            }}
                                            SelectProps={{
                                                MenuProps: {
                                                    PaperProps: {
                                                        sx: {
                                                            maxHeight: 320,
                                                            bgcolor: OBSIDIAN.surfaceContainerHigh,
                                                            backgroundImage: 'none',
                                                            border: `1px solid ${OBSIDIAN.outline}44`,
                                                            '& .MuiMenuItem-root': {
                                                                color: OBSIDIAN.onSurface,
                                                                fontSize: 13,
                                                                fontWeight: 600,
                                                                '&.Mui-selected': {
                                                                    bgcolor: `${OBSIDIAN.primary}22`,
                                                                    color: OBSIDIAN.primary,
                                                                    fontWeight: 800,
                                                                },
                                                                '&.Mui-selected:hover': {
                                                                    bgcolor: `${OBSIDIAN.primary}33`,
                                                                },
                                                                '&:hover': {
                                                                    bgcolor: OBSIDIAN.surfaceContainer,
                                                                },
                                                            },
                                                        },
                                                    },
                                                },
                                            }}>
                                            {timezoneOptions.map(tz => {
                                                const local = getBrowserTimezone();
                                                const label =
                                                    tz === local
                                                        ? `${formatTimezoneOptionLabel(tz)} · Local`
                                                        : formatTimezoneOptionLabel(tz);
                                                return (
                                                    <MenuItem key={tz} value={tz}>
                                                        {label}
                                                    </MenuItem>
                                                );
                                            })}
                                        </TextField>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                                        <TextField
                                            label="Buffer before (min)"
                                            type="number"
                                            value={bufferBefore}
                                            onChange={e => setBufferBefore(Math.max(0, Number(e.target.value) || 0))}
                                            inputProps={{ min: 0, step: 5 }}
                                            fullWidth
                                            helperText="Gap before each session"
                                            FormHelperTextProps={{ sx: { color: OBSIDIAN.onSurfaceVariant } }}
                                            sx={officeHoursFieldSx}
                                        />
                                        <TextField
                                            label="Buffer after (min)"
                                            type="number"
                                            value={bufferAfter}
                                            onChange={e => setBufferAfter(Math.max(0, Number(e.target.value) || 0))}
                                            inputProps={{ min: 0, step: 5 }}
                                            fullWidth
                                            helperText="Gap after each session"
                                            FormHelperTextProps={{ sx: { color: OBSIDIAN.onSurfaceVariant } }}
                                            sx={officeHoursFieldSx}
                                        />
                                    </Box>

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={pricingEnabled}
                                                onChange={e => setPricingEnabled(e.target.checked)}
                                                sx={{
                                                    color: OBSIDIAN.outline,
                                                    '&.Mui-checked': { color: OBSIDIAN.primary },
                                                }}
                                            />
                                        }
                                        label="Charge for office hours by default"
                                        sx={{
                                            '& .MuiFormControlLabel-label': {
                                                fontSize: 13,
                                                color: OBSIDIAN.onSurface,
                                                fontFamily: OBSIDIAN_BODY_FONT,
                                            },
                                        }}
                                    />
                                    {pricingEnabled ? (
                                        <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                                            <TextField
                                                label="Default price (USD)"
                                                value={defaultPrice}
                                                onChange={e => setDefaultPrice(e.target.value)}
                                                fullWidth
                                                sx={officeHoursFieldSx}
                                            />
                                            <TextField
                                                select
                                                label="Billing"
                                                value={billing}
                                                onChange={e => setBilling(e.target.value as 'per_slot' | 'per_hour')}
                                                fullWidth
                                                sx={officeHoursFieldSx}>
                                                <MenuItem value="per_slot">Per slot</MenuItem>
                                                <MenuItem value="per_hour">Per hour (× slot length)</MenuItem>
                                            </TextField>
                                        </Box>
                                    ) : null}

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={active}
                                                onChange={e => setActive(e.target.checked)}
                                                sx={{
                                                    color: OBSIDIAN.outline,
                                                    '&.Mui-checked': { color: OBSIDIAN.primary },
                                                }}
                                            />
                                        }
                                        label="Accepting bookings"
                                        sx={{
                                            mt: 0.5,
                                            '& .MuiFormControlLabel-label': {
                                                fontSize: 13,
                                                color: OBSIDIAN.onSurface,
                                                fontFamily: OBSIDIAN_BODY_FONT,
                                            },
                                        }}
                                    />
                                </Box>

                                <Box sx={panelSx}>
                                    {stepKicker('02', 'Standard cadence')}
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: OBSIDIAN.onSurfaceVariant,
                                            mb: 1.5,
                                            fontFamily: OBSIDIAN_BODY_FONT,
                                        }}>
                                        Weekly hours in {timezone}
                                    </Typography>
                                    <SandboxOfficeHoursWeeklyEditor value={availability} onChange={setAvailability} />
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    ...panelSx,
                                    minWidth: 0,
                                    height: 'fit-content',
                                    position: { md: 'sticky' },
                                    top: 0,
                                }}>
                                {stepKicker('03', 'Blackout & overrides')}
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: OBSIDIAN.onSurfaceVariant,
                                        mb: 1.5,
                                        fontFamily: OBSIDIAN_BODY_FONT,
                                    }}>
                                    Override weekly hours for specific dates — open with custom times, or block the day.
                                </Typography>
                                <SandboxOfficeHoursOverridesEditor value={dateOverrides} onChange={setDateOverrides} />
                            </Box>
                        </Box>
                    )}

                    {viewTab === 'setup' ? (
                        <Box
                            sx={{
                                flexShrink: 0,
                                px: { xs: 2, md: 3, lg: 4 },
                                py: 1.75,
                                borderTop: `1px solid ${OBSIDIAN.outline}33`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                flexWrap: 'wrap',
                                bgcolor: 'rgba(30,30,30,0.75)',
                                backdropFilter: 'blur(10px)',
                                WebkitBackdropFilter: 'blur(10px)',
                                width: '100%',
                                boxSizing: 'border-box',
                            }}>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        fontWeight: 800,
                                        letterSpacing: '0.08em',
                                        textTransform: 'uppercase',
                                        color: OBSIDIAN.onSurfaceVariant,
                                    }}>
                                    Configuration summary
                                </Typography>
                                <Typography
                                    sx={{ fontSize: 13, color: OBSIDIAN.onSurface, fontFamily: OBSIDIAN_BODY_FONT }}>
                                    Active: {activeDays} days/week · {dateOverrides.length} overrides
                                    {googleConnected ? ' · Google connected' : ' · Google not connected'}
                                </Typography>
                            </Box>
                            <Button
                                disabled={saving}
                                onClick={() => void save()}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 800,
                                    borderRadius: 999,
                                    px: 2.75,
                                    py: 1.25,
                                    bgcolor: OBSIDIAN.primary,
                                    color: OBSIDIAN.onPrimaryFixed,
                                }}>
                                {saving ? 'Saving…' : 'Save configuration'}
                            </Button>
                        </Box>
                    ) : null}
                </>
            )}
        </Dialog>
    );
}
